import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ShieldCheck, Target, Gauge, ReceiptText, BarChart3,
  Layers, Scale, Quote, Check, Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.06 } }),
};

const EXPERIENCE = [
  { icon: BarChart3, stat: "15+ Years", label: "Marketing & Communications Experience" },
  { icon: Scale, stat: "Law-Firm Marketing", label: "Firsthand experience managing legal marketing programs and vendor relationships" },
  { icon: Layers, stat: "Multi-Channel Marketing", label: "Experience across digital advertising, paid media, lead generation, traditional advertising and integrated campaigns" },
  { icon: ReceiptText, stat: "Vendor Oversight", label: "Experience evaluating agency performance, contracts, invoices, deliverables and reporting" },
  { icon: Gauge, stat: "Performance Analytics", label: "Experience translating marketing activity into actionable business intelligence" },
  { icon: Target, stat: "Marketing Technology", label: "Experience building and using technology to improve marketing operations, reporting and accountability" },
];

const BELIEVES = [
  "Marketing spend", "Vendor deliverables", "Leads and inquiries", "Intake performance",
  "Retained matters", "Cost per retained case", "Revenue, when available and appropriate",
  "Overall marketing efficiency",
];

const PILLARS = [
  { title: "Independent", body: "Our analysis is not tied to the performance of a particular marketing vendor or advertising platform." },
  { title: "Outcome-Focused", body: "We look beyond marketing activity to examine what happens as leads move toward retained matters." },
  { title: "Evidence-Based", body: "Findings are based on the data, documentation and information available from the firm and its marketing partners." },
  { title: "Executive-Friendly", body: "Our goal is to turn complicated marketing information into clear findings leadership can understand and act on." },
];

const Section = ({ children, className = "", ...rest }) => (
  <section className={`relative z-10 border-t border-white/10 ${className}`} {...rest}>
    <div className="max-w-5xl mx-auto px-6 py-20 sm:py-24">{children}</div>
  </section>
);

const Eyebrow = ({ children }) => (
  <p className="font-mono-data text-[11px] uppercase tracking-[0.25em] text-[#D5A44A] mb-5">{children}</p>
);

export default function About() {
  const navigate = useNavigate();
  return (
    <div className="dark relative min-h-screen bg-[#0A1B2E] text-[#F5F5F5] font-body overflow-hidden" data-testid="about-page">
      <Seo title="About RetainProof — Why we built independent marketing accountability" description="RetainProof is an independent marketing performance intelligence layer for law firms — reconciling spend, vendor reporting and retained-case outcomes so leadership sees the complete picture." path="/about" />
      <div className="grain-overlay" />

      {/* Nav */}
      <nav className="fixed top-0 inset-x-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 font-body font-bold text-2xl tracking-tight" data-testid="brand-logo">
            <img src="/retainproof-icon-dark.png" alt="RetainProof" className="h-8 w-auto" />
            <span>Retain<span className="text-[#0E7C7B]">Proof</span><sup className="text-[0.5em] font-normal align-super">™</sup></span>
          </Link>
          <div className="flex items-center gap-1 sm:gap-3">
            <Link to="/features" data-testid="nav-features-link">
              <Button variant="ghost" className="hidden sm:inline-flex text-zinc-300 hover:text-white hover:bg-white/5 rounded-none">Features</Button>
            </Link>
            <a href="/#assessments" data-testid="nav-assessments-link">
              <Button variant="ghost" className="hidden sm:inline-flex text-zinc-300 hover:text-white hover:bg-white/5 rounded-none">Assessments</Button>
            </a>
            <Link to="/login" data-testid="nav-login-link">
              <Button variant="ghost" className="text-zinc-300 hover:text-white hover:bg-white/5 rounded-none">Sign in</Button>
            </Link>
            <Button onClick={() => navigate("/register")} data-testid="nav-register-link" className="bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none font-medium">Get started</Button>
          </div>
        </div>
      </nav>

      {/* 1. Why RetainProof Exists */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 pt-40 pb-20 sm:pb-24">
        <motion.div initial="hidden" animate="show" variants={fadeUp} custom={0}>
          <Eyebrow>About RetainProof</Eyebrow>
        </motion.div>
        <motion.h1 initial="hidden" animate="show" variants={fadeUp} custom={1}
          className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight" data-testid="about-hero-title">
          Why RetainProof Exists
        </motion.h1>
        <motion.p initial="hidden" animate="show" variants={fadeUp} custom={2}
          className="mt-7 font-display text-2xl sm:text-3xl font-light text-zinc-300 max-w-3xl leading-snug">
          RetainProof was created to answer a deceptively simple question: <span className="italic text-[#D5A44A]">what is your marketing actually producing?</span>
        </motion.p>
        <motion.div initial="hidden" animate="show" variants={fadeUp} custom={3}
          className="mt-8 space-y-5 text-lg text-zinc-400 max-w-3xl leading-relaxed">
          <p>Law firms invest significantly in agencies, advertising platforms, lead generators, media, sponsorships, digital campaigns and other marketing channels. Each vendor may provide its own reporting, but those reports rarely tell the entire story.</p>
          <p>RetainProof was created from firsthand experience managing law-firm marketing budgets, vendors, campaigns and performance reporting—and seeing how difficult it can be to connect what is being reported to what the firm is actually retaining.</p>
          <p className="text-white">We built RetainProof to provide an independent layer of marketing accountability and performance intelligence.</p>
        </motion.div>
      </section>

      {/* 2. Built From Experience */}
      <Section className="bg-[#0B1F33]" data-testid="about-origin">
        <Eyebrow>Our origin</Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight">Built From Experience</h2>
        <div className="mt-8 grid lg:grid-cols-[1.4fr,1fr] gap-12">
          <div className="space-y-5 text-zinc-400 leading-relaxed">
            <p>RetainProof began with a real-world marketing management problem. While overseeing law-firm marketing operations, our founder encountered discrepancies between marketing expenditures, vendor reporting and the business outcomes the firm could actually verify.</p>
            <p>The deeper the review went, the clearer the problem became. Marketing performance was being evaluated largely through information supplied by the same vendors responsible for delivering the marketing.</p>
            <p>That experience became the foundation for RetainProof. We developed a methodology for bringing marketing spend, vendor reporting, lead activity and retained matters together so leadership can evaluate performance from a more complete and independent perspective.</p>
          </div>
          <div className="border-l-2 border-[#0E7C7B] pl-6 space-y-4 self-center">
            {[
              "Clicks could be counted.",
              "Calls could be reported.",
              "Leads could be submitted.",
              "Campaign activity could be documented.",
            ].map((t) => (
              <p key={t} className="font-display text-xl text-zinc-300">{t}</p>
            ))}
            <p className="font-display text-xl text-white pt-2">But the question that mattered most to firm leadership was much harder to answer: <span className="text-[#D5A44A]">what did we spend—and what did we actually retain because of it?</span></p>
          </div>
        </div>
      </Section>

      {/* 3. Marketing Reporting Should Connect to Business Outcomes */}
      <Section data-testid="about-beliefs">
        <Eyebrow>What we believe</Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight max-w-3xl">Marketing Reporting Should Connect to Business Outcomes</h2>
        <p className="mt-6 text-lg text-zinc-400 max-w-3xl leading-relaxed">Marketing metrics have value. But impressions, clicks, calls and leads should not exist in isolation from what happens next. RetainProof helps firms look beyond activity metrics to examine the relationship between:</p>
        <div className="mt-8 grid sm:grid-cols-2 gap-x-10 gap-y-3">
          {BELIEVES.map((b) => (
            <div key={b} className="flex items-center gap-3 border-b border-white/10 pb-3">
              <Check className="h-4 w-4 text-[#D5A44A] shrink-0" /><span className="text-zinc-300">{b}</span>
            </div>
          ))}
        </div>
        <p className="mt-8 text-zinc-400 max-w-3xl leading-relaxed">The goal is not to discredit marketing agencies or vendors. <span className="text-white">The goal is to give firm leadership an independent view of the complete picture.</span></p>
      </Section>

      {/* 4. The Experience Behind RetainProof */}
      <Section className="bg-[#0B1F33]" data-testid="about-experience">
        <Eyebrow>Credentials</Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight">The Experience Behind RetainProof</h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {EXPERIENCE.map(({ icon: Icon, stat, label }) => (
            <div key={stat} className="border border-white/10 bg-[#12324D] p-6" data-testid="about-experience-card">
              <Icon className="h-6 w-6 text-[#D5A44A]" />
              <p className="font-display text-xl mt-4 leading-tight">{stat}</p>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{label}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 text-sm text-zinc-500 max-w-3xl">Our experience spans marketing strategy, communications, vendor management, digital advertising, analytics, operations and marketing technology.</p>
      </Section>

      {/* 5. Independent by Design */}
      <Section data-testid="about-independence">
        <Eyebrow>Independence</Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight">Independent by Design</h2>
        <div className="mt-8 grid lg:grid-cols-2 gap-10">
          <ul className="space-y-3">
            {[
              "RetainProof is not a marketing agency.",
              "We do not sell media.",
              "We do not manage advertising budgets.",
              "We do not receive commissions for recommending marketing vendors.",
              "We are not trying to replace the agencies and specialists already supporting your firm.",
            ].map((t) => (
              <li key={t} className="flex gap-3 text-zinc-300"><ShieldCheck className="h-5 w-5 text-[#0E7C7B] shrink-0 mt-0.5" />{t}</li>
            ))}
          </ul>
          <div className="border-l-2 border-[#D5A44A] pl-6 self-center space-y-4 text-zinc-400 leading-relaxed">
            <p className="text-white font-display text-2xl">Our role is different.</p>
            <p>RetainProof provides an independent layer of analysis between marketing activity and firm leadership. We examine the information available, reconcile what can be verified, identify gaps and inconsistencies, and help leadership ask better questions about marketing performance.</p>
          </div>
        </div>
      </Section>

      {/* 6. Founder Quote */}
      <Section className="bg-[#0B1F33]" data-testid="about-quote">
        <Quote className="h-10 w-10 text-[#D5A44A]/40" />
        <blockquote className="mt-6 font-display text-2xl sm:text-3xl lg:text-4xl font-light leading-snug tracking-tight max-w-4xl">
          "I didn't build RetainProof because law firms needed another marketing dashboard. I built it because leadership needed a clearer way to understand the relationship between what they were spending, what they were being told, and what the firm was actually retaining."
        </blockquote>
        <p className="mt-8 font-mono-data text-xs uppercase tracking-[0.2em] text-zinc-500">— Founder, RetainProof</p>
      </Section>

      {/* 7. What Makes RetainProof Different */}
      <Section data-testid="about-methodology">
        <Eyebrow>Methodology</Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight">What Makes RetainProof Different</h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PILLARS.map((p, i) => (
            <div key={p.title} className="border-t-2 border-[#D5A44A] bg-[#12324D] p-6">
              <p className="font-mono-data text-[10px] text-zinc-600">0{i + 1}</p>
              <p className="font-display text-xl mt-2">{p.title}</p>
              <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* 8. A Note About Our Leadership */}
      <Section className="bg-[#0B1F33]" data-testid="about-leadership-note">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-4">
            <Lock className="h-4 w-4 text-zinc-500" />
            <p className="font-mono-data text-[11px] uppercase tracking-[0.2em] text-zinc-500">A Note About Our Leadership</p>
          </div>
          <p className="text-zinc-400 leading-relaxed">RetainProof maintains a deliberately low-profile public leadership presence. This allows our public-facing work to remain centered on the methodology, analysis and results rather than personal branding.</p>
          <p className="text-zinc-400 leading-relaxed mt-4">Clients engaging RetainProof receive appropriate company, engagement and contact information during the consultation, contracting and onboarding process.</p>
        </div>
      </Section>

      {/* 9. Assessment CTA */}
      <Section data-testid="about-cta">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-tight">
            You don't need more marketing data. <span className="text-[#D5A44A]">You need a clearer view of what it means.</span>
          </h2>
          <p className="mt-6 text-lg text-zinc-400 leading-relaxed">RetainProof helps law-firm leadership independently evaluate marketing spend, vendor performance and retained-case outcomes.</p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a href="/#assessments" data-testid="about-cta-assessment">
              <Button className="bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none h-12 px-8 text-base font-medium group">
                Explore the Marketing Performance Assessment
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </a>
            <Link to="/consultation" data-testid="about-cta-consult">
              <Button variant="outline" className="rounded-none h-12 px-8 text-base border-white/25 text-white bg-transparent hover:bg-white/5">Schedule a Consultation</Button>
            </Link>
          </div>
        </div>
      </Section>

      <footer className="relative z-10 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-zinc-500">
          <span>© 2026 RetainProof™ · A product of CMD Marketing Enterprises LLC</span>
          <div className="flex gap-6">
            <Link to="/" className="hover:text-white">Home</Link>
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
